import React, { useState } from "react";
import { toast } from "sonner";
import panelApi from "@/lib/panelApi";
import { useAcenteAuth } from "@/context/AcenteAuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import PasswordInput from "@/components/PasswordInput";
import { KeyRound, User, Mail, Save, Building2 } from "lucide-react";

/**
 * Acente Ayarları — profil özeti + kendi şifre değiştirme.
 * Backend endpoint: POST /api/panel/me/password { old_password, new_password }.
 */
export default function AcenteSettings() {
  const { user } = useAcenteAuth();
  const [pwd, setPwd] = useState({ old_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password.length < 6) {
      toast.error("Yeni şifre en az 6 karakter olmalı");
      return;
    }
    if (pwd.new_password !== pwd.confirm) {
      toast.error("Yeni şifre ve tekrarı eşleşmiyor");
      return;
    }
    setBusy(true);
    try {
      await panelApi.post("/me/password", {
        old_password: pwd.old_password,
        new_password: pwd.new_password,
      });
      toast.success("Şifreniz güncellendi");
      setPwd({ old_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Şifre güncellenemedi");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="acente-settings">
      <header>
        <div className="text-xs uppercase tracking-[0.3em] text-brand-orange font-semibold mb-2">Hesap Ayarları</div>
        <h1 className="font-serif text-3xl text-brand-navy">Ayarlar</h1>
        <p className="text-sm text-slate-600 mt-1">Hesap bilgilerinizi görüntüleyin ve şifrenizi güncelleyin.</p>
      </header>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Profile summary */}
        <div className="bg-white rounded-2xl border border-brand-navy/10 p-6 shadow-sm space-y-4" data-testid="settings-profile">
          <div className="flex items-center gap-3 pb-2 border-b border-slate-100">
            <div className="w-11 h-11 rounded-xl bg-brand-orange/15 text-brand-orange-dark flex items-center justify-center">
              <User size={20} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500">Profil</div>
              <div className="font-serif text-lg text-brand-navy">Hesap Bilgileri</div>
            </div>
          </div>
          <div className="space-y-3 text-sm">
            <Row icon={<User size={14} />} label="Ad Soyad" value={user?.name || "—"} />
            <Row icon={<Mail size={14} />} label="E-posta" value={user?.email} testid="settings-email" />
            <Row icon={<KeyRound size={14} />} label="Rol" value={{
              admin: "Yönetici",
              acente: "Acente",
              operasyon: "Operasyon",
            }[user?.role] || user?.role} />
            {user?.agency_name && <Row icon={<Building2 size={14} />} label="Acente" value={user.agency_name} />}
          </div>
        </div>

        {/* Change password */}
        <form onSubmit={changePassword} className="bg-white rounded-2xl border border-brand-navy/10 p-6 shadow-sm space-y-4" data-testid="settings-password-form">
          <div className="flex items-center gap-3 pb-2 border-b border-slate-100">
            <div className="w-11 h-11 rounded-xl bg-brand-navy/10 text-brand-navy flex items-center justify-center">
              <KeyRound size={20} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500">Güvenlik</div>
              <div className="font-serif text-lg text-brand-navy">Şifre Değiştir</div>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <Label>Mevcut Şifre</Label>
              <PasswordInput required value={pwd.old_password} onChange={(e) => setPwd({ ...pwd, old_password: e.target.value })} autoComplete="current-password" data-testid="settings-old-pwd" />
            </div>
            <div>
              <Label>Yeni Şifre (en az 6 karakter)</Label>
              <PasswordInput required minLength={6} value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} autoComplete="new-password" data-testid="settings-new-pwd" />
            </div>
            <div>
              <Label>Yeni Şifre (Tekrar)</Label>
              <PasswordInput required value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} autoComplete="new-password" data-testid="settings-confirm-pwd" />
            </div>
          </div>
          <Button type="submit" disabled={busy} className="bg-brand-orange text-white hover:bg-brand-orange-dark w-full" data-testid="settings-save-pwd">
            <Save size={14} className="mr-2" /> {busy ? "Kaydediliyor..." : "Şifreyi Güncelle"}
          </Button>
          <p className="text-[11px] text-slate-500">
            Şifrenizi unuttuysanız çıkış yapıp giriş ekranındaki <b>Şifremi Unuttum</b> bağlantısını kullanın; yöneticimiz sizi telefondan arayarak şifrenizi yeniler.
          </p>
        </form>
      </div>
    </div>
  );
}

function Row({ icon, label, value, testid }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-slate-100 bg-canvas-cream/50 px-3 py-2">
      <div className="text-brand-orange">{icon}</div>
      <div className="text-xs uppercase tracking-widest text-slate-500 w-24">{label}</div>
      <div className="text-brand-navy font-medium truncate" data-testid={testid}>{value}</div>
    </div>
  );
}
